// Shared client-side validation + save-error classification.

export const MIN_DATE = '1900-01-01';
export const MAX_DATE = '2100-12-31';

// True when a date value is present but fails calendar or range validation.
// Empty is treated as valid here (required-ness is handled elsewhere).
// min/max are optional YYYY-MM-DD strings; defaults are MIN_DATE / MAX_DATE.
export function isDateOutOfRange(value, min, max) {
  if (value === undefined || value === null) return false;
  const str = String(value).trim();
  if (str === '') return false;
  const m = /^(\d+)-(\d{2})-(\d{2})$/.exec(str);
  if (!m) return true;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) return true;
  if (day < 1) return true;
  // Calendar-aware: catches Feb 30, Apr 31, etc. (including leap years).
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return true;
  // Compare against the effective range, using numeric year bounds first so
  // years with an unusual digit count (e.g. 20255) are always caught.
  const effectiveMin = min || MIN_DATE;
  const effectiveMax = max || MAX_DATE;
  const minYear = Number(effectiveMin.split('-')[0]);
  const maxYear = Number(effectiveMax.split('-')[0]);
  if (year < minYear || year > maxYear) return true;
  const dateStr = String(year).padStart(4, '0') + '-' + m[2] + '-' + m[3];
  if (dateStr < effectiveMin || dateStr > effectiveMax) return true;
  return false;
}

// True when a present numeric value is non-numeric or falls outside [min, max].
// Empty is treated as valid (required-ness is handled elsewhere). min/max are
// optional; an omitted bound is not enforced.
// A value Salesforce will accept as a number: digits, one optional decimal
// point, an optional leading minus, optional exponent. Nothing else.
//
// The point of the anchors is what `parseFloat` does NOT do. parseFloat reads
// as far as it can and returns what it got, so `parseFloat('3%')` is 3 and
// `parseFloat('5 years')` is 5. Every check built on it therefore passed values
// that Apex's JSON parser then refused outright, taking the whole record with
// it: on 2026-09-18 an advisor typed a percent sign into a number field at
// 7:17 AM and her household stopped saving for the rest of the morning
// ("Character % is neither a decimal digit number, decimal point, nor \"e\"
// notation exponential mark"). A partially typed value like '3.' is withheld
// too, and comes through on the next keystroke, which is how the date guard
// already behaves.
export const NUMERIC_RE = /^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][-+]?\d+)?$/;

/** True when a non-empty value is not a number Salesforce could store. */
export function isNotNumeric(value) {
  if (value === undefined || value === null) return false;
  const str = String(value).replace(/,/g, '').trim();
  if (str === '') return false;
  return !NUMERIC_RE.test(str);
}

export function isNumberOutOfRange(value, min, max) {
  if (value === undefined || value === null) return false;
  const str = String(value).trim();
  if (str === '') return false;
  // Anything that is not a clean number is flagged, which is what this already
  // did for 'abc' (parseFloat → NaN) and now also does for '3%' and '5 years'.
  // Without it those sat unflagged in a red-free field while the record silently
  // refused every save.
  if (isNotNumeric(str)) return true;
  const n = parseFloat(str.replace(/,/g, ''));
  if (min != null && n < min) return true;
  if (max != null && n > max) return true;
  return false;
}

// Strip everything that isn't part of a decimal number — currency symbols,
// thousands commas, spaces, stray letters — keeping a single leading minus and
// a single decimal point so partially-typed values ('-', '1.') survive. Used by
// the numeric inputs so a stray '$' or 'abc' can't silently blank or, worse,
// store garbage that reads as $0 (QA: "$1,000 abc" wiped a home's value).
// Evaluate a typed arithmetic expression, e.g. "=8400*12" to annualize a
// monthly figure on the fly. Advisors do this arithmetic in their heads today
// while a client waits, and asked for it independently at the 7/31 demos and
// the 8/3 feedback call.
//
// Hand-rolled recursive descent rather than eval/new Function: the app's CSP
// sets `script-src 'self'` with no unsafe-eval, so both are blocked in
// production, and neither belongs anywhere near a user-typed string regardless.
// Supports + - * / , parentheses, decimals and unary minus. Returns a finite
// number, or null when the expression is empty, malformed or divides by zero.
export function evaluateFormula(raw) {
  let src = String(raw == null ? '' : raw).trim();
  if (src.startsWith('=')) src = src.slice(1);
  // Tolerate the characters people actually type around a number.
  src = src.replace(/[$,\s]/g, '');
  if (src === '') return null;
  // Reject anything outside the supported grammar before parsing.
  if (!/^[0-9.+\-*/()]+$/.test(src)) return null;

  let i = 0;
  const peek = () => src[i];
  const eat = ch => { if (src[i] === ch) { i += 1; return true; } return false; };

  // expression := term (('+' | '-') term)*
  function expression() {
    let left = term();
    if (left == null) return null;
    for (;;) {
      if (eat('+')) {
        const r = term();
        if (r == null) return null;
        left += r;
      } else if (eat('-')) {
        const r = term();
        if (r == null) return null;
        left -= r;
      } else return left;
    }
  }

  // term := factor (('*' | '/') factor)*
  function term() {
    let left = factor();
    if (left == null) return null;
    for (;;) {
      if (eat('*')) {
        const r = factor();
        if (r == null) return null;
        left *= r;
      } else if (eat('/')) {
        const r = factor();
        if (r == null || r === 0) return null; // no divide by zero
        left /= r;
      } else return left;
    }
  }

  // factor := '-' factor | '(' expression ')' | number
  function factor() {
    if (eat('-')) {
      const v = factor();
      return v == null ? null : -v;
    }
    if (eat('(')) {
      const v = expression();
      if (v == null || !eat(')')) return null;
      return v;
    }
    const start = i;
    while (i < src.length && /[0-9.]/.test(peek())) i += 1;
    if (i === start) return null;
    const n = parseFloat(src.slice(start, i));
    return Number.isFinite(n) ? n : null;
  }

  const value = expression();
  // Trailing junk means the whole thing is malformed, not partly usable.
  if (value == null || i !== src.length || !Number.isFinite(value)) return null;
  // Money: two decimals is the most precision any of these fields carry.
  return Math.round(value * 100) / 100;
}

// True when a typed string is meant as a formula rather than a plain number.
export function looksLikeFormula(raw) {
  const s = String(raw == null ? '' : raw).trim();
  return s.startsWith('=') || /[+*/()]/.test(s.replace(/^-/, ''));
}

export function sanitizeNumericInput(raw) {
  let s = String(raw == null ? '' : raw).replace(/[^0-9.\-]/g, '');
  const negative = s.startsWith('-');
  s = s.replace(/-/g, '');
  if (negative) s = '-' + s;
  const dot = s.indexOf('.');
  if (dot !== -1) {
    s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
  }
  return s;
}

// True when an email value is present but not a plausible address. Empty is
// treated as valid (required-ness is handled elsewhere). Deliberately lenient —
// a single @ with a dotted domain — so it flags obvious typos ("bademail",
// "name@") without rejecting valid-but-unusual addresses.
export function isInvalidEmail(value) {
  if (value === undefined || value === null) return false;
  const str = String(value).trim();
  if (str === '') return false;
  return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

// Format a US phone number for display on blur: 10 digits -> (123) 456-7890,
// 11 digits starting with 1 -> 1 (123) 456-7890. Anything else (a partial entry
// or an international number) is returned trimmed but unformatted so a number we
// don't recognize is never mangled.
export function formatPhone(value) {
  const raw = String(value == null ? '' : value);
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw.trim();
}

// True when a phone value is present but isn't a plausible US number (10 digits,
// or 11 starting with 1). Empty is valid — nothing is required to save. Lenient
// and non-blocking, mirroring the email check: it surfaces an obvious typo
// without rejecting an unusual-but-valid entry.
export function isInvalidPhone(value) {
  if (value === undefined || value === null) return false;
  const str = String(value).trim();
  if (str === '') return false;
  const digits = str.replace(/\D/g, '');
  if (digits.length === 10) return false;
  if (digits.length === 11 && digits[0] === '1') return false;
  return true;
}

// True when an end date is present and falls before a present start date.
// Both are YYYY-MM-DD, which compares correctly as plain strings.
export function isEndBeforeStart(startStr, endStr) {
  const s = String(startStr || '').trim();
  const e = String(endStr || '').trim();
  if (!s || !e) return false;
  return e < s;
}

export const DATE_RANGE_HINT = 'Enter a valid date between 1900 and 2100.';

// Compute the min/max bounds (as YYYY-MM-DD strings) for a date field based on
// its dateRange tag. Called at render/validation time so the bounds always
// reflect the current date. Used across the PQ and Plan Order forms.
export function getDateRange(dateRange) {
  const now = new Date();
  const y = now.getFullYear();
  const mo = now.getMonth();
  const day = now.getDate();
  const iso = dt => dt.toISOString().slice(0, 10);
  const today = iso(now);
  switch (dateRange) {
    case 'recent': // Plan Order: documents email date — past year to ~1 month out
      return { min: iso(new Date(y - 1, mo, day)), max: iso(new Date(y, mo + 1, day)) };
    case 'birthdate': // date of birth — up to 120 years ago, never future
      return { min: iso(new Date(y - 120, mo, day)), max: today };
    case 'retirement': // retirement dates — 40 years either side of today
      return { min: iso(new Date(y - 40, mo, day)), max: iso(new Date(y + 40, mo, day)) };
    case 'historical': // policy / issue / acquisition dates — past 100 years, never future
      return { min: iso(new Date(y - 100, mo, day)), max: today };
    case 'planning': // income / expense start & end — broad planning horizon
      return { min: iso(new Date(y - 60, mo, day)), max: iso(new Date(y + 60, mo, day)) };
    default:
      return { min: MIN_DATE, max: MAX_DATE };
  }
}

export function getDateRangeHint(dateRange) {
  switch (dateRange) {
    case 'recent': return 'Date must be within the past year.';
    case 'birthdate': return 'Enter a valid date of birth — within the past 120 years, not in the future. Type the year as four digits (e.g. 1965).';
    case 'retirement': return 'Date must be within 40 years of today.';
    case 'historical': return 'Enter a date within the past 100 years.';
    case 'planning': return 'Enter a date within 60 years of today.';
    default: return DATE_RANGE_HINT;
  }
}

// Classify a save failure so the UI can tell the advisor whether THEY can fix
// it (a validation problem such as a bad date or a missing field) or whether
// it is a system problem to report to IT.
//   400 / 422 -> Salesforce rejected the data (validation / DML). User-fixable.
//   anything else (401/403 auth, 404, 5xx, or no status from a network error)
//   -> system problem.
export function classifySaveError(status, rejectedByServer = false) {
  // Salesforce rejects a save with HTTP 200 and a failure flag in the body, so
  // status alone misreads those as a system fault and tells the advisor to
  // raise a ticket for something they could fix themselves (Jimmy,
  // 2026-08-26). `rejectedByServer` carries that body-level verdict.
  if (rejectedByServer) return 'validation';
  if (status === 400 || status === 422) return 'validation';
  return 'system';
}

const WARN = '⚠';

export const SAVE_MSG_VALIDATION =
  `${WARN} Some of your entries were not accepted. Please review the highlighted ` +
  `fields (check that any dates are valid and required fields and selections are ` +
  `filled in). It will save automatically once corrected.`;

// Shown when Salesforce refused one or more specific values and the app has
// saved everything else. The old message could only say "review the highlighted
// fields" and then highlight nothing, because nothing parsed the rejection.
export function saveMsgQuarantined(labels) {
  const list = labels.length === 1 ? labels[0] : labels.join(', ');
  const s = labels.length === 1 ? '' : 's';
  return (
    `${WARN} Salesforce would not accept what was entered in ${list}. ` +
    `Everything else on the form HAS been saved. Edit the field${s} below and ` +
    `it will save automatically. Check for unusual symbols, or letters in a ` +
    `number field, and try shortening very long text.`
  );
}

export const SAVE_MSG_SYSTEM =
  `${WARN} Auto-save failed. Your entries have NOT reached Salesforce yet, but ` +
  `nothing is lost while this tab stays open. The app keeps retrying on its own, ` +
  `and you can click Save to retry right now. If this message does not clear ` +
  `within a few minutes, submit a ticket to IT.`;

// Required-field (name) messages. Shown when a manual Save is attempted before
// the client's name is filled in. The client first + last name are required to
// create/update the Salesforce Lead, so we block the save and say which is
// missing rather than failing silently.
export const SAVE_MSG_NAME_FIRST =
  `${WARN} Please enter the client's first name before saving.`;
export const SAVE_MSG_NAME_LAST =
  `${WARN} Please enter the client's last name before saving.`;
export const SAVE_MSG_NAME_BOTH =
  `${WARN} Please enter the client's first and last name before saving.`;

export const SEND_MSG_VALIDATION =
  `${WARN} Some entries on the Plan Order were not accepted. Please review the ` +
  `highlighted fields (check that any dates are valid and selections are filled ` +
  `in), then click Retry.`;

export const SEND_MSG_SYSTEM =
  `${WARN} We could not send the Plan Order right now, but your entries are safe ` +
  `on this device. Please keep this browser tab open and submit a ticket to IT ` +
  `so they can help. You can also click Retry to try again.`;
